import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const LAST_UPDATED = 'May 2025';

const sections = [
  { id: 'introduction',   title: '1. Introduction' },
  { id: 'who-we-are',     title: '2. Who We Are' },
  { id: 'what-we-collect', title: '3. Data We Collect' },
  { id: 'what-we-dont',   title: '4. Data We Do NOT Collect' },
  { id: 'how-we-use',     title: '5. How We Use Your Data' },
  { id: 'lawful-basis',   title: '6. Lawful Basis (DPDP Act)' },
  { id: 'our-responsibility', title: '7. Our Data Responsibilities' },
  { id: 'not-responsible', title: '8. Outside Our Scope' },
  { id: 'sharing',        title: '9. Data Sharing' },
  { id: 'retention',      title: '10. Data Retention' },
  { id: 'cookies',        title: '11. Cookies & Storage' },
  { id: 'security',       title: '12. Security' },
  { id: 'your-rights',    title: '13. Your Rights' },
  { id: 'children',       title: '14. Children\'s Privacy' },
  { id: 'third-party',    title: '15. Third-Party Links' },
  { id: 'changes',        title: '16. Policy Updates' },
  { id: 'contact',        title: '17. Contact & Grievances' },
];

const Section = ({ id, title, children }) => (
  <div id={id} className="mb-12 scroll-mt-24">
    <h2 style={{ color: '#0D1F3C', fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #FEF9E7' }}>
      {title}
    </h2>
    <div style={{ color: '#374151', lineHeight: '1.75', fontSize: '0.9375rem' }}>
      {children}
    </div>
  </div>
);

const P = ({ children }) => <p style={{ marginBottom: '0.875rem' }}>{children}</p>;

const BulletList = ({ items }) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 1rem' }}>
    {items.map((item, i) => (
      <li key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <span style={{ color: '#1A6FD6', fontWeight: 700, flexShrink: 0, marginTop: '0.1rem' }}>—</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Highlight = ({ title, children, type = 'info' }) => {
  const styles = {
    info:    { bg: '#EFF6FF', border: '#BFDBFE', icon: 'ℹ', iconColor: '#1A6FD6' },
    success: { bg: '#F0FDF4', border: '#BBF7D0', icon: '✓', iconColor: '#16A34A' },
    warning: { bg: '#FFFBEB', border: '#FDE68A', icon: '!', iconColor: '#D97706' },
    red:     { bg: '#FEF2F2', border: '#FECACA', icon: '✕', iconColor: '#DC2626' },
  };
  const s = styles[type];
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
        <span style={{ color: s.iconColor, fontWeight: 700, fontSize: '1rem', flexShrink: 0, marginTop: '0.1rem' }}>{s.icon}</span>
        <div>
          {title && <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.25rem' }}>{title}</p>}
          <p style={{ margin: 0, color: '#374151', fontSize: '0.9rem', lineHeight: '1.6' }}>{children}</p>
        </div>
      </div>
    </div>
  );
};

const DataTable = ({ rows }) => (
  <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
      <thead>
        <tr style={{ background: '#F1F5F9' }}>
          {rows[0].map((h, i) => (
            <th key={i} style={{ padding: '0.625rem 0.875rem', textAlign: 'left', fontWeight: 700, color: '#0D1F3C', border: '1px solid #E5E9F0', whiteSpace: 'nowrap' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.slice(1).map((row, ri) => (
          <tr key={ri} style={{ background: ri % 2 === 0 ? '#ffffff' : '#FAFAFA' }}>
            {row.map((cell, ci) => (
              <td key={ci} style={{ padding: '0.625rem 0.875rem', border: '1px solid #E5E9F0', color: '#374151', verticalAlign: 'top' }}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('introduction');

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 120;
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i].id);
        if (el && el.offsetTop <= scrollY) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{ background: '#FFFBEB', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      {/* Top Bar */}
      <div style={{ background: '#ffffff', borderBottom: '1px solid #E5E9F0', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontWeight: 600, fontSize: '0.875rem' }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 12L6 8l4-4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back to Successlane
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#1A6FD6' }}></div>
            <span style={{ fontWeight: 700, color: '#0D1F3C', fontSize: '0.9rem' }}>Successlane</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2.5rem 1.5rem' }}>

        {/* Page Header */}
        <div style={{ marginBottom: '2.5rem' }}>
          <p style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#1A6FD6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Legal</p>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#0D1F3C', marginBottom: '0.5rem', lineHeight: 1.2 }}>Privacy Policy</h1>
          <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>Last updated: {LAST_UPDATED} &nbsp;·&nbsp; Operated by <strong>Success Lane</strong></p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '3rem', alignItems: 'start' }}>

          {/* Sticky Sidebar Nav */}
          <nav style={{ position: 'sticky', top: '80px', background: '#ffffff', borderRadius: '1rem', border: '1px solid #E5E9F0', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>Contents</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {sections.map(s => (
                <li key={s.id}>
                  <button
                    onClick={() => scrollTo(s.id)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '0.4rem 0.625rem', borderRadius: '0.375rem',
                      background: activeSection === s.id ? '#EFF6FF' : 'none',
                      color: activeSection === s.id ? '#1A6FD6' : '#6B7280',
                      fontWeight: activeSection === s.id ? 600 : 400,
                      fontSize: '0.8125rem', border: 'none', cursor: 'pointer',
                      lineHeight: 1.4, transition: 'all 0.15s',
                    }}
                  >
                    {s.title}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content */}
          <div>

            <Section id="introduction" title="1. Introduction">
              <P>At <strong>Successlane</strong>, we take your privacy seriously. This Privacy Policy explains — in plain language — what personal information we collect when you use successlane.io, why we collect it, how it is used and stored, who we share it with, and what rights you have over your own data.</P>
              <P>This policy applies to all visitors and users of successlane.io, including job seekers using our free evaluation or paid rewrite services, professionals applying to join our recruiter network, and anyone who browses our website.</P>
              <P>By using our platform, you consent to the practices described in this policy. If you do not agree, please do not use our services.</P>
              <Highlight type="info" title="Plain-language summary">
                We collect only what we need to deliver our service. We don't sell your data, don't use advertising trackers, and don't access your LinkedIn account. Your data is kept for 2 years and then deleted. You can request access, correction, or deletion of your data at any time.
              </Highlight>
            </Section>

            <Section id="who-we-are" title="2. Who We Are">
              <P><strong>Success Lane</strong>, a registered partnership firm ("we", "us", "our"), is the legal entity that owns and operates the Successlane platform at successlane.io. Under India's Digital Personal Data Protection Act, 2023 (DPDP Act), we are the <strong>Data Fiduciary</strong> responsible for the personal data collected through this platform.</P>
              <div style={{ background: '#ffffff', border: '1px solid #E5E9F0', borderRadius: '0.75rem', padding: '1.25rem 1.5rem', display: 'inline-block', minWidth: '280px' }}>
                <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.5rem' }}>Success Lane (Registered Partnership Firm)</p>
                <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Operating as: <strong>Successlane</strong></p>
                <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '0.25rem' }}>21/22, T2, 3rd Floor, Girinagar 2nd Phase,</p>
                <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Bangalore, Karnataka 560085, India</p>
                <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Website: <a href="https://successlane.io" style={{ color: '#1A6FD6' }}>successlane.io</a></p>
                <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Email: <a href="mailto:contact@successlane.io" style={{ color: '#1A6FD6' }}>contact@successlane.io</a></p>
                <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>Phone: <a href="tel:+917259444336" style={{ color: '#1A6FD6' }}>+91 72594 44336</a></p>
              </div>
            </Section>

            <Section id="what-we-collect" title="3. Data We Collect">
              <P>We collect only the data that is necessary to provide our services. Here is a complete breakdown by user type:</P>

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>3.1 Free Evaluation Users</p>
              <P>When you request a free LinkedIn evaluation, we collect:</P>
              <DataTable rows={[
                ['Data', 'How Collected', 'Why'],
                ['LinkedIn profile data (name, headline, experience, education, skills, about section, current company, location)', 'Via LinkedIn URL import (Bright Data API) or PDF upload — you choose', 'To generate your personalised evaluation'],
                ['Target role', 'You type it into the form', 'To personalise the keyword and gap analysis'],
                ['Target companies / industry', 'Optional — you type it in', 'To further personalise recommendations'],
                ['WhatsApp number', 'You enter it; an OTP is sent for verification', 'Identity verification + to send you evaluation updates and notifications'],
                ['Consent record', 'Checkbox ticked by you before data import', 'Required under DPDP Act before processing your LinkedIn data'],
              ]} />

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>3.2 Paid Rewrite Customers</p>
              <P>At checkout, we additionally collect:</P>
              <BulletList items={[
                'Full name, email address, phone number, country, and state (for order management and delivery)',
              ]} />
              <P>After payment, via the intake form, we collect:</P>
              <BulletList items={[
                'Target job title and seniority level',
                'Total years of professional experience',
                'Current and target industry',
                'Up to 5 dream companies you are targeting',
                'A job description you optionally paste (for keyword matching)',
                'Your LinkedIn profile data (re-imported or re-uploaded)',
                'Up to 3 career achievements with specific metrics',
                'Key skills and tools you want highlighted',
                'Any additional context — awards, certifications, career pivots (optional)',
              ]} />

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>3.3 Recruiter Applicants</p>
              <P>If you apply to join our recruiter network, we collect:</P>
              <BulletList items={[
                'Name, email address, and phone number',
                'Current company and job title',
                'LinkedIn profile URL (for verification)',
                'Years of hiring experience',
                'Industries and seniority levels you specialise in',
                'A short note on why you want to join (optional)',
              ]} />

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>3.4 Website Usage Data</p>
              <P>We collect anonymised, non-identifying event data when you interact with our website, such as which buttons you click and on which page. This is processed by our own in-house analytics system. <strong>We do not use Google Analytics, Meta Pixel, Mixpanel, or any third-party advertising or analytics tools.</strong></P>
            </Section>

            <Section id="what-we-dont" title="4. Data We Do NOT Collect or Store">
              <Highlight type="red" title="We are explicit about what we don't do">
                The following data is never collected, stored, or accessed by Successlane — full stop.
              </Highlight>
              <BulletList items={[
                'Your LinkedIn password or any login credentials — we never ask for them and our import process does not require them',
                'Your credit or debit card number, CVV, or full payment details — all payment data is handled exclusively by Razorpay',
                'Real-time or ongoing access to your LinkedIn account — profile imports are a one-time read of publicly available data',
                'Biometric data, government ID numbers, Aadhaar, PAN, or any sensitive personal identifiers',
                'Your precise GPS location or device location data',
                'Data from third-party social media accounts other than your public LinkedIn profile',
                'Advertising or behavioural profiles for targeting purposes',
              ]} />
            </Section>

            <Section id="how-we-use" title="5. How We Use Your Data">
              <P>We use the data we collect only for the purposes listed below. We do not use your data for any purpose beyond what is described here without your prior consent.</P>
              <DataTable rows={[
                ['Data', 'Purpose'],
                ['LinkedIn profile data', 'To generate your free evaluation and/or execute your paid rewrite'],
                ['Target role & dream companies', 'To personalise keyword strategy, gap analysis, and rewrite content'],
                ['WhatsApp number', 'OTP verification during sign-up, plus order updates and delivery notifications'],
                ['Name, email, phone', 'Order management, delivery, customer support, and service communications'],
                ['Intake form responses', 'To complete your personalised LinkedIn rewrite'],
                ['Recruiter application data', 'To review, onboard, and manage recruiter network partners'],
                ['Website event data (anonymised)', 'To understand how our platform is used and improve the experience'],
              ]} />
            </Section>

            <Section id="lawful-basis" title="6. Lawful Basis for Processing (DPDP Act 2023)">
              <P>Under India's <strong>Digital Personal Data Protection Act, 2023</strong>, we are required to have a lawful basis for processing your personal data. We process your data on the following bases:</P>
              <BulletList items={[
                'Consent — you explicitly tick a consent checkbox before your LinkedIn profile data is imported. You may withdraw consent at any time by contacting us, though this may prevent us from delivering the service.',
                'Contractual necessity — processing is required to fulfil the service you have purchased (e.g., we cannot rewrite your LinkedIn profile without your profile data and intake responses).',
                'Legitimate interests — anonymised website analytics help us understand how users interact with the platform, improve our product, and allocate resources responsibly.',
              ]} />
              <P>As the <strong>Data Fiduciary</strong> under the DPDP Act, we are committed to processing your data fairly, lawfully, and transparently — collecting only what is necessary, and only for the purposes stated in this policy.</P>
            </Section>

            <Section id="our-responsibility" title="7. Our Data Responsibilities">
              <Highlight type="success" title="What we commit to doing">
                This section outlines our concrete commitments regarding how we handle your personal data.
              </Highlight>
              <BulletList items={[
                'We collect only the minimum data necessary to deliver our services — nothing more.',
                'We obtain your explicit consent before importing your LinkedIn profile data.',
                'We keep your data secure using HTTPS/TLS encryption for all data in transit.',
                'We do not sell, rent, or share your personal data with advertisers, data brokers, or any third party not directly involved in delivering your service.',
                'We share your intake data only with the specific recruiter assigned to certify your profile, and only for that purpose.',
                'We retain your data for a maximum of 2 years, after which it is deleted or anonymised.',
                'We respond to data access, correction, and deletion requests within a reasonable timeframe.',
                'We notify you if there is a data breach that materially affects your personal information.',
                'We do not use your data to build advertising profiles or retargeting audiences.',
              ]} />
            </Section>

            <Section id="not-responsible" title="8. Outside Our Scope — What We Are Not Responsible For">
              <Highlight type="warning" title="Important limitations">
                This section clarifies the boundaries of our data responsibility.
              </Highlight>
              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem' }}>8.1 Third-Party Platforms</p>
              <P>We are not responsible for the privacy practices of LinkedIn, Razorpay, Bright Data, WhatsApp, or any other third-party platform. Once data is transmitted to these platforms under their respective terms, it is governed by their privacy policies — not ours. We encourage you to read their policies before use.</P>

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>8.2 Data You Share Publicly</p>
              <P>We import only the data available on your public LinkedIn profile. If your LinkedIn profile is publicly visible, that data can be accessed by anyone — including us. We are not responsible for the public availability of information you have chosen to share on LinkedIn.</P>

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>8.3 Actions of Recruiter Partners</p>
              <P>While we require recruiter partners to treat your data confidentially, we cannot control the actions of independent professionals beyond the obligations in our partner agreements. If you believe a recruiter has misused your data, please contact us immediately at contact@successlane.io.</P>

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>8.4 Data You Share with Others After Delivery</p>
              <P>Once your rewritten LinkedIn profile and Playbook are delivered to you, how you use, share, or distribute them is entirely your responsibility. We are not accountable for data shared by you with third parties after delivery.</P>

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>8.5 Unauthorised Access Despite Security Measures</p>
              <P>We implement robust security measures, but no system is completely immune to breaches. We cannot guarantee absolute security against sophisticated attacks. In the unlikely event of a breach affecting your personal data, we will notify you as required by law and take immediate remedial action.</P>
            </Section>

            <Section id="sharing" title="9. Data Sharing — Who We Share With and Why">
              <P>We share your personal data only in the following specific circumstances:</P>

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem' }}>9.1 Assigned Recruiter (Service Delivery)</p>
              <P>For paid rewrite orders, your LinkedIn profile data, intake form responses, and rewritten content draft are shared with the recruiter assigned to certify your profile. This is essential to fulfil the service. Recruiters are contractually bound to use your data only for certification purposes and to keep it confidential.</P>

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>9.2 Razorpay (Payment Processing)</p>
              <P>Your name, email, phone number, and country are shared with <strong>Razorpay</strong> to process your ₹999 payment. Razorpay is PCI-DSS Level 1 compliant — the highest standard for payment security. Successlane does not receive or store your card details.</P>

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>9.3 Bright Data (LinkedIn Profile Import)</p>
              <P>When you provide your LinkedIn URL for profile import, that URL is sent to <strong>Bright Data</strong> — a third-party data provider — to retrieve your publicly available LinkedIn profile information. No login credentials are involved. Bright Data processes this data under their own privacy policy.</P>

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>9.4 Legal Obligations</p>
              <P>We may disclose your information where required to do so by law, court order, or governmental authority. We will notify you of such requests where legally permitted to do so.</P>

              <Highlight type="success">
                We do not share, sell, rent, or trade your personal data with any other third party for any commercial purpose — including advertisers, data brokers, or analytics companies.
              </Highlight>
            </Section>

            <Section id="retention" title="10. Data Retention">
              <P>We retain your personal data for <strong>2 years</strong> from the date your order is completed, your last login, or your last interaction with our platform — whichever is most recent.</P>
              <P>After the 2-year period, your personal data is either permanently deleted from our systems or anonymised (stripped of all identifying information) so it can no longer be linked back to you.</P>
              <P>Exceptions where we may retain data longer:</P>
              <BulletList items={[
                'Where required by applicable Indian law (e.g., accounting records, legal holds)',
                'Where you have an active dispute or legal claim with us',
              ]} />
              <P>To request deletion of your data before the 2-year period, email us at <a href="mailto:contact@successlane.io" style={{ color: '#1A6FD6' }}>contact@successlane.io</a>. We will process your request within a reasonable timeframe, subject to any legal obligations that require us to retain certain records.</P>
            </Section>

            <Section id="cookies" title="11. Cookies & Browser Storage">
              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem' }}>11.1 What We Use</p>
              <DataTable rows={[
                ['Type', 'What It Stores', 'Duration', 'Purpose'],
                ['HttpOnly Cookie (server-set)', 'Authenticated session token', 'Session / until logout', 'Secure login for admin and recruiter portal users only'],
                ['Browser Session Storage', 'Evaluation ID, results, form progress', 'Cleared when tab is closed', 'Preserve your progress during a session so you don\'t lose work'],
              ]} />

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>11.2 What We Don't Use</p>
              <BulletList items={[
                'No advertising or retargeting cookies',
                'No third-party analytics cookies (Google Analytics, Meta Pixel, etc.)',
                'No persistent tracking cookies across sessions',
                'No fingerprinting or supercookies',
              ]} />

              <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>11.3 Your Control</p>
              <P>Because we use only strictly necessary session-based storage and no third-party cookies, a cookie consent banner is not currently required. You may clear your browser's session storage at any time through your browser settings — this will simply require you to re-enter any forms you were partway through.</P>
            </Section>

            <Section id="security" title="12. How We Protect Your Data">
              <P>We implement the following safeguards to protect your personal information:</P>
              <BulletList items={[
                'All data transmitted between your browser and our servers is encrypted using HTTPS/TLS',
                'Payment processing is handled entirely by Razorpay (PCI-DSS Level 1 compliant) — we never receive or store your card details',
                'Access to personal data in our admin systems requires authenticated login using secure, HttpOnly session tokens — not accessible via browser scripts',
                'Our team members access only the data they need to deliver your service',
                'We do not store payment card data on our own servers under any circumstances',
              ]} />
              <P>While we take every reasonable measure to protect your data, no system can guarantee absolute security. In the unlikely event of a data breach that materially affects your personal information, we will notify you as required under the DPDP Act and take immediate corrective action.</P>
            </Section>

            <Section id="your-rights" title="13. Your Rights Under the DPDP Act 2023">
              <P>As a <strong>Data Principal</strong> under India's Digital Personal Data Protection Act, 2023, you have the following rights with respect to your personal data:</P>
              <DataTable rows={[
                ['Right', 'What It Means'],
                ['Right to Access', 'You may request a summary of the personal data we hold about you and how it is being used.'],
                ['Right to Correction', 'You may request that we correct any inaccurate, incomplete, or outdated personal data.'],
                ['Right to Erasure', 'You may request that we delete your personal data. We will do so within our 2-year retention policy and any legal obligations.'],
                ['Right to Withdraw Consent', 'You may withdraw your consent to data processing at any time. Note: withdrawal may prevent us from delivering the service if work has not yet begun.'],
                ['Right to Grievance Redressal', 'You have the right to raise a complaint about how your data has been handled, and to receive a timely response.'],
                ['Right to Nominate', 'You may nominate another individual to exercise these rights on your behalf in the event of your death or incapacity.'],
              ]} />
              <P>To exercise any of these rights, email us at <a href="mailto:contact@successlane.io" style={{ color: '#1A6FD6' }}>contact@successlane.io</a> with your name, email address on file, and a description of your request. We will respond within a reasonable timeframe as required under applicable law.</P>
            </Section>

            <Section id="children" title="14. Children's Privacy">
              <P>Successlane's services are intended exclusively for working professionals aged <strong>18 years and above</strong>. We do not knowingly collect, store, or process personal data from individuals under the age of 18.</P>
              <P>If we become aware that we have inadvertently collected data from a minor, we will delete it immediately. If you believe a minor's data has been submitted to our platform, please contact us at contact@successlane.io.</P>
            </Section>

            <Section id="third-party" title="15. Third-Party Links">
              <P>Our website may contain links to or integrations with third-party platforms, including LinkedIn, Razorpay, and WhatsApp. These platforms operate independently and are governed by their own privacy policies.</P>
              <P>Successlane is not responsible for the privacy practices, content, or security of any third-party website or service. We encourage you to review their privacy policies before providing any personal information to them directly.</P>
            </Section>

            <Section id="changes" title="16. Updates to This Privacy Policy">
              <P>We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or the services we offer.</P>
              <P>When we make material changes, we will:</P>
              <BulletList items={[
                'Update the "Last updated" date at the top of this page',
                'Notify registered users via email where reasonably practicable',
                'Post a visible notice on our website for a reasonable period following the change',
              ]} />
              <P>Your continued use of our services after changes are posted constitutes your acceptance of the updated Privacy Policy. If you do not agree with the updated policy, please discontinue use of our services and contact us to request deletion of your data.</P>
            </Section>

            <Section id="contact" title="17. Contact & Grievance Redressal">
              <P>For any privacy questions, data requests, complaints, or to exercise your rights under the DPDP Act, please reach out to us:</P>
              <div style={{ background: '#ffffff', border: '1px solid #E5E9F0', borderRadius: '0.75rem', padding: '1.25rem 1.5rem', display: 'inline-block', minWidth: '280px', marginBottom: '1rem' }}>
                <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.5rem' }}>Data Grievance Contact</p>
                <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Company: <strong>Success Lane (Registered Partnership Firm)</strong></p>
                <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Brand: <strong>Successlane</strong></p>
                <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '0.25rem' }}>21/22, T2, 3rd Floor, Girinagar 2nd Phase,</p>
                <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Bangalore, Karnataka 560085, India</p>
                <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Website: <a href="https://successlane.io" style={{ color: '#1A6FD6' }}>successlane.io</a></p>
                <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Email: <a href="mailto:contact@successlane.io" style={{ color: '#1A6FD6' }}>contact@successlane.io</a></p>
                <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>Phone: <a href="tel:+917259444336" style={{ color: '#1A6FD6' }}>+91 72594 44336</a></p>
              </div>
              <P>We aim to acknowledge all privacy-related queries within 2 business days and resolve them within 30 days, in line with our obligations under the DPDP Act, 2023.</P>
            </Section>

            {/* Footer note */}
            <div style={{ borderTop: '1px solid #E5E9F0', paddingTop: '2rem', marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <p style={{ color: '#9CA3AF', fontSize: '0.8125rem' }}>© 2025 Success Lane · Successlane</p>
              <div style={{ display: 'flex', gap: '1.5rem' }}>
                <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontSize: '0.8125rem', fontWeight: 500 }}>Terms of Service</button>
                <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: '0.8125rem' }}>Back to Home</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
